import { Button } from '@/components/ui/button';
import type { CustomPageConfig, SettingsState } from '@/hooks/useSettings';
import { Plus, Trash2 } from 'lucide-react';
import { supportedLanguages } from '@/utils/languageUtils';
import { getDefaultPromptTemplate } from '@/utils/promptUtils';
import { PromptType } from '@/types/common';
import { useTranslation } from 'react-i18next';

interface CustomPagesSettingsProps {
    settings: SettingsState;
    updateSetting: (category: string, key: string, value: any) => void;
}

const RESERVED_ROUTES = new Set(['', 'knowledge', 'questions', 'chat', 'settings', 'api-key-guide', 'auth']);

function slugifyRoute(value: string) {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function getUniqueRoute(route: string, pages: CustomPageConfig[], currentId: string) {
    const usedRoutes = new Set(
        pages
            .filter(page => page.id !== currentId)
            .map(page => page.route)
    );
    let nextRoute = slugifyRoute(route);
    let suffix = 2;

    if (RESERVED_ROUTES.has(nextRoute)) {
        nextRoute = `${nextRoute || 'page'}-${suffix}`;
        suffix += 1;
    }

    while (usedRoutes.has(nextRoute) || RESERVED_ROUTES.has(nextRoute)) {
        nextRoute = `${nextRoute.replace(/-\d+$/, '')}-${suffix}`;
        suffix += 1;
    }

    return nextRoute;
}

export default function CustomPagesSettings({ settings, updateSetting }: CustomPagesSettingsProps) {
    const { t } = useTranslation();
    const customPages = settings.customPages || [];
    const inputClass = 'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5';
    const textareaClass = `${inputClass} min-h-40 font-mono leading-relaxed`;

    const promptLanguages = supportedLanguages;

    const savePages = (pages: CustomPageConfig[]) => {
        updateSetting('', 'customPages', pages);
    };

    const addPage = () => {
        savePages([
            ...customPages,
            {
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                title: '',
                route: '',
                sheetName: '',
                customPromptTemplates: {}
            }
        ]);
    };

    const removePage = (id: string) => {
        savePages(customPages.filter(page => page.id !== id));
    };

    const updatePage = (id: string, key: keyof CustomPageConfig, value: string) => {
        const updatedPages = customPages.map(page => {
            if (page.id !== id) return page;

            const nextPage = {
                ...page,
                [key]: key === 'route' ? slugifyRoute(value) : value
            };

            if (key === 'title' && !page.route) {
                nextPage.route = slugifyRoute(value);
            }

            if (key === 'route') {
                nextPage.route = getUniqueRoute(value, customPages, id);
            }

            return nextPage;
        });

        savePages(updatedPages);
    };

    const updateBuiltInPromptTemplate = (pageKey: 'knowledge' | 'interview', language: string, value: string) => {
        updateSetting('', 'pagePromptTemplates', {
            ...(settings.pagePromptTemplates || {}),
            [pageKey]: {
                ...(settings.pagePromptTemplates?.[pageKey] || {}),
                [language]: value
            }
        });
    };

    const updatePromptTemplate = (id: string, language: string, value: string) => {
        savePages(customPages.map(page => {
            if (page.id !== id) return page;

            return {
                ...page,
                customPromptTemplates: {
                    ...(page.customPromptTemplates || {}),
                    [language]: value
                }
            };
        }));
    };

    const normalizePages = () => {
        savePages(customPages
            .map(page => ({
                ...page,
                title: page.title.trim(),
                route: getUniqueRoute(page.route || page.title || page.sheetName, customPages, page.id),
                sheetName: page.sheetName.trim()
            }))
            .filter(page => page.title && page.route && page.sheetName)
        );
    };

    const renderPromptEditors = (
        values: Record<string, string> | undefined,
        onChange: (language: string, value: string) => void,
        promptType: PromptType
    ) => (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {promptLanguages.map((language) => (
                <div key={language.value}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('settings.customPages.promptLabel', {
                            language: `${language.flag} ${language.label}`
                        })}
                    </label>
                    <textarea
                        value={values?.[language.value] || ''}
                        onChange={(event) => onChange(language.value, event.target.value)}
                        className={textareaClass}
                        placeholder={getDefaultPromptTemplate(promptType, language.value)}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        {t('settings.customPages.promptHelp')}
                    </p>
                </div>
            ))}
        </div>
    );

    return (
        <section className="border rounded-md p-4 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-lg font-medium">{t('settings.customPages.title')}</h3>
                    <p className="text-sm text-muted-foreground">
                        {t('settings.customPages.description')}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={normalizePages}>
                        {t('settings.customPages.normalize')}
                    </Button>
                    <Button type="button" size="sm" onClick={addPage}>
                        <Plus className="w-4 h-4 mr-2" />
                        {t('settings.customPages.addPage')}
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border p-3 space-y-3">
                <div>
                    <h4 className="font-medium">{t('settings.customPages.builtInKnowledge')}</h4>
                    <p className="text-sm text-muted-foreground">{t('settings.customPages.builtInDescription')}</p>
                </div>
                {renderPromptEditors(
                    settings.pagePromptTemplates?.knowledge,
                    (language, value) => updateBuiltInPromptTemplate('knowledge', language, value),
                    PromptType.KNOWLEDGE
                )}
            </div>

            <div className="rounded-lg border p-3 space-y-3">
                <div>
                    <h4 className="font-medium">{t('settings.customPages.builtInInterview')}</h4>
                    <p className="text-sm text-muted-foreground">{t('settings.customPages.builtInDescription')}</p>
                </div>
                {renderPromptEditors(
                    settings.pagePromptTemplates?.interview,
                    (language, value) => updateBuiltInPromptTemplate('interview', language, value),
                    PromptType.INTERVIEW
                )}
            </div>

            <div className="space-y-3">
                {customPages.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                        {t('settings.customPages.empty')}
                    </div>
                )}

                {customPages.map((page) => (
                    <div key={page.id} className="rounded-lg border p-3 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,1fr,auto] gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.customPages.pageName')}</label>
                                <input
                                    type="text"
                                    value={page.title}
                                    onChange={(event) => updatePage(page.id, 'title', event.target.value)}
                                    className={inputClass}
                                    placeholder={t('settings.customPages.pageNamePlaceholder')}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.customPages.route')}</label>
                                <input
                                    type="text"
                                    value={page.route}
                                    onChange={(event) => updatePage(page.id, 'route', event.target.value)}
                                    onBlur={(event) => updatePage(page.id, 'route', event.target.value)}
                                    className={inputClass}
                                    placeholder={t('settings.customPages.routePlaceholder')}
                                />
                                {page.route && (
                                    <p className="mt-1 text-xs text-gray-500">/custom/{page.route}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.customPages.sheetName')}</label>
                                <input
                                    type="text"
                                    value={page.sheetName}
                                    onChange={(event) => updatePage(page.id, 'sheetName', event.target.value)}
                                    className={inputClass}
                                    placeholder={t('settings.customPages.sheetNamePlaceholder')}
                                />
                            </div>
                            <div className="flex md:items-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => removePage(page.id)}
                                    className="w-full md:w-10"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {renderPromptEditors(
                            page.customPromptTemplates,
                            (language, value) => updatePromptTemplate(page.id, language, value),
                            PromptType.KNOWLEDGE
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}
